import React from "react"
import { useFlags } from "launchdarkly-react-client-sdk"
import { Redirect, useLocation, useParams } from "react-router-dom"
import { useIntl } from "react-intl"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { helpCenterArticlePath, parseArticleIdFromPath } from "#/services/help-center/urls"
import { useHelpCenterArticle } from "#/services/help-center/useGetHelpCenter"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { Markdown, defaultMarkdownElements } from "#/services/markdown"
import { HelpCenterArticleNavState, isHelpCenterArticleNavState } from "#/services/help-center/types"
import { FaqsBreadcrumbs } from "#/pages/faqs/components/FaqsBreadcrumbs"
import { usePageViewTracking } from "#/services/analytics/usePageViewTracking"
import { faqsLabelMessage } from "#/pages/faqs/messages"

const ARTICLE_CONTENT_MAX_INLINE_SIZE = "606px"

export function FaqsArticle(): JSX.Element {
  const { contentfulHelpCenter } = useFlags()

  if (!contentfulHelpCenter) {
    return <Redirect to="/" />
  }

  return <FaqsArticleContent />
}

function FaqsArticleContent(): JSX.Element {
  const intl = useIntl()
  const { vanityAndId } = useParams<{ vanityAndId: string }>()
  const articleId = parseArticleIdFromPath(vanityAndId)
  const location = useLocation()
  const navState = isHelpCenterArticleNavState(location.state) ? location.state : null
  const { data: article, isLoading, isError, error } = useHelpCenterArticle(articleId)
  const productTitle = intl.formatMessage(faqsLabelMessage)

  const canonicalPath = article ? helpCenterArticlePath(article) : null
  const isOnCanonicalPath = canonicalPath !== null && location.pathname === canonicalPath
  usePageViewTracking({
    enabled: isOnCanonicalPath,
    metadata: article
      ? {
          articleId: article.id,
          articleTitle: article.title,
          categorySlug: navState?.from.categorySlug,
          categoryTitle: navState?.from.categoryTitle
        }
      : undefined
  })

  if (!articleId) {
    return <Redirect to={Paths.FAQS} />
  }

  if (isLoading) {
    return <FullScreenLoading />
  }

  if (isError) {
    if (error?.statusCode === 404) {
      return <Redirect to={Paths.FAQS} />
    }
    return (
      <PageLayout>
        <ErrorPage />
      </PageLayout>
    )
  }

  if (!article) {
    return <Redirect to={Paths.FAQS} />
  }

  return (
    <>
      {!isOnCanonicalPath && <Redirect to={{ ...location, pathname: helpCenterArticlePath(article) }} />}
      <Title title={article.title} productTitle={productTitle} />
      <PageLayout header={<ArticleBreadcrumbHeader navState={navState} />}>
        <Box sx={{ maxInlineSize: ARTICLE_CONTENT_MAX_INLINE_SIZE }}>
          <defaultMarkdownElements.h1.component {...defaultMarkdownElements.h1.props}>
            {article.title}
          </defaultMarkdownElements.h1.component>
          {article.description ? (
            <Typography variant="body1" sx={(theme) => ({ paddingBlockEnd: theme.spacing(theme.tokens.spacing.md) })}>
              {article.description}
            </Typography>
          ) : null}
          <Markdown>{article.body}</Markdown>
        </Box>
      </PageLayout>
    </>
  )
}

function ArticleBreadcrumbHeader({ navState }: { navState: HelpCenterArticleNavState | null }): JSX.Element {
  const category = navState ? { title: navState.from.categoryTitle, slug: navState.from.categorySlug } : undefined
  return <PageBreadcrumbHeader breadcrumbs={<FaqsBreadcrumbs category={category} />} />
}
